import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import {
  DEFAULT_PAGINATION,
  errorMessage,
  successMessage,
} from "../../../utils";
import { AppContext } from "../../../store";
import {
  deleteSegmentApi,
  exportSegmentCustomers,
  getSegmentationBoot,
  getSegmentById,
  getSegmentCustomersApi,
  rerunSegment,
} from "../api";

export const useSegmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { appState } = useContext(AppContext);
  const currentTab = appState.currentTabs.segmentDetails ?? "1";
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [state, setState] = useImmer({
    isLoading: false,
    isExporting: false,
    isRerunning: false,
    data: {},
    operators: {},
    criterias: {},
    referenceTypes: [],
    previewLoading: false,
    previewCustomers: [],
    previewPagination: DEFAULT_PAGINATION,
    searchText: "",
    isSegmentDeleting: false,
    isSystem: true, // Defaulting to true to prevent any accidental deletes/edits before data is loaded
  });

  const loadCustomers = async (
    pageIndex = DEFAULT_PAGINATION.pageIndex,
    pageSize = DEFAULT_PAGINATION.pageSize,
    searchText = "",
  ) => {
    setState((draft) => {
      draft.previewLoading = true;
    });
    try {
      const query = searchText ? { params: { search: searchText } } : {};
      const res = await getSegmentCustomersApi(
        query,
        { pageIndex, pageSize },
        id,
      );
      if (res.success) {
        setState((draft) => {
          draft.previewCustomers = res.data.customers ?? [];
          draft.previewPagination = res.data.pagination ?? DEFAULT_PAGINATION;
          draft.searchText = searchText;
        });
      } else {
        errorMessage(res.message ?? "Failed to load customers.");
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message ?? "Failed to load customers.");
    } finally {
      setState((draft) => {
        draft.previewLoading = false;
      });
    }
  };

  const loadSegment = async () => {
    setState((draft) => {
      draft.isLoading = true;
    });
    try {
      const [segRes, bootRes] = await Promise.all([
        getSegmentById(id),
        getSegmentationBoot(),
      ]);
      if (segRes.success) {
        setState((draft) => {
          draft.data = segRes.data;
          draft.isSystem = segRes.data?.is_system;
        });
        await loadCustomers(
          DEFAULT_PAGINATION.pageIndex,
          DEFAULT_PAGINATION.pageSize,
          state.searchText,
        );
      } else {
        errorMessage(segRes.message ?? "Failed to load segment.");
      }
      if (bootRes.success) {
        setState((draft) => {
          draft.operators = bootRes.data.operators ?? {};
          draft.criterias = bootRes.data.criterias ?? {};
          draft.referenceTypes = bootRes.data.reference_types ?? [];
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message ?? "Failed to load segment.");
    } finally {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  useEffect(() => {
    if (id && initialLoad) {
      loadSegment();
      setInitialLoad(false);
    }
  }, []); // Added to fix eslint warning. No actual need for this effect.

  useEffect(() => {
    if (id && currentTab === "1" && !initialLoad) loadSegment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentTab]);

  const onPreviewGotoPage = (pageIndex) =>
    loadCustomers(
      pageIndex,
      state.previewPagination.pageSize,
      state.searchText,
    );

  const onPreviewSetPageSize = (e) =>
    loadCustomers(1, +e.target.value, state.searchText);

  const onPreviewSearch = (searchText) =>
    loadCustomers(
      DEFAULT_PAGINATION.pageIndex,
      state.previewPagination.pageSize,
      searchText,
    );

  const onExport = async () => {
    setState((draft) => {
      draft.isExporting = true;
    });
    try {
      const res = await exportSegmentCustomers(id);
      successMessage(
        res.message ?? "Export in-progress. You will be emailed once complete.",
      );
    } catch (err) {
      errorMessage(err?.response?.data?.message ?? "Export failed.");
    } finally {
      setState((draft) => {
        draft.isExporting = false;
      });
    }
  };

  const onRerun = async () => {
    setState((draft) => {
      draft.isRerunning = true;
    });
    try {
      const res = await rerunSegment(id);
      const { success, message } = res;
      if (success) {
        successMessage(message);
        if (currentTab === "1") await loadSegment();
      } else {
        errorMessage(message ?? "Rerun failed.");
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message ?? "Rerun failed.");
    } finally {
      setState((draft) => {
        draft.isRerunning = false;
      });
    }
  };

  const deleteSegment = async (segmentId) => {
    const fallbackMessage = "Failed to delete segment.";
    setState((draft) => {
      draft.isSegmentDeleting = true;
    });
    try {
      const response = await deleteSegmentApi(segmentId);
      const { success, message } = response;
      if (success) {
        successMessage(message);
        navigate("/customer-segmentation");
      } else {
        errorMessage(message || fallbackMessage);
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error?.message || fallbackMessage;
      errorMessage(message);
    } finally {
      setState((draft) => {
        draft.isSegmentDeleting = false;
      });
    }
  };

  const onEdit = () => navigate(`/customer-segmentation/details/${id}/edit`);

  const breadcrumbLinks = React.useMemo(
    () => [
      { name: "Dashboard", href: "/" },
      { name: "Customer Segments", href: "/customer-segmentation" },
      { name: state.data.name || "Segment Details" },
    ],
    [state.data.name],
  );

  return {
    state,
    id,
    breadcrumbLinks,
    onExport,
    onEdit,
    onRerun,
    loadSegment,
    onPreviewGotoPage,
    onPreviewSetPageSize,
    onPreviewSearch,
    deleteSegment,
  };
};
